import {
  ActiveXboardingEmployeeIds,
  DeactivationCauseEnum,
  Employee as EmployeeDto,
  EmployeeDirectoryListing as EmployeeDirectoryListingDto,
  EmployeeStatusChangeLogResponse as EmployeeStatusChangeLogResponseDto,
  EmployeeLeaveResponse as EmployeeLeaveResponseDto,
  PaginatedResponseEmployeeLeaveResponse as PaginatedResponseEmployeeLeaveResponseDto,
  CreateEmployeeLeaveRequest,
  Group as GroupDto,
  EmployeeStatusEnum,
  EmployeeApplicationAccountResponse as EmployeeApplicationAccountResponseDto,
  BulkEmployeeApplicationAccountGetResponse,
  BulkEmployeesResponse as BulkEmployeesResponseDto,
  BulkGetEmployeeLeavesResponse as BulkGetEmployeeLeavesResponseDto,
  EmployeeLeaveStatusEnum,
} from '@electricjs/core_entity-client';
import { ApiListResponseDto } from 'externalTypes/general';

import { TagTypes, apiSlice } from '@/redux/slices/apiSlice';
import { providesListAndAllById } from '@/redux/slices/helpers/cacheTagHelpers';
import {
  BulkEmployeeApplicationAccountsResponse,
  BulkEmployeeApplicationAccountsArgs,
  BulkEmployeesArgs,
  BulkEmployeesResponse,
  DesktopAppInstructionsArgs,
  Employee,
  EmployeeDirectoryListing,
  EmployeeApplicationAccount,
  EmployeeSource,
  EmployeeStatusUpdateArgs,
  EmployeeUpdateArgs,
  ListEmployeeApplicationAccountsArgs,
  GetEmployeeByEmailArgs,
  MDMEnrollInstructionsArgs,
  ListEmployeeApplicationAccountsResponse,
  GetEmployeeByIdArgs,
  GetApplicationEmployeeAccountsArgs,
  ApplicationEmployeeAccountsWithEmployeesResponse,
  EmployeeStatusChangeLogResponse,
  EmployeeLeaveResponse,
  EmployeeLeave,
} from '@/types/employees';
import { GetGroupsForEmployeeArgs, Group } from '@/types/groups';
import { OrganizationUserRoleSlugs } from '@/types/users';

import {
  invalidatesList,
  invalidatesListAndById,
  providesById,
} from './helpers/cacheTagHelpers';
import { Address } from '@electricjs/arc';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { organizationApiSlice } from '@/redux/slices/organizationApiSlice';

// A leave create/update/end/cancel can trigger a champion reassignment on
// the backend; these are the tags every champion-display/selector query is
// keyed on (see organizationApiSlice.ts and requestApiSlice.ts).
const CHAMPION_INVALIDATION_TAGS = [
  { type: TagTypes.OrganizationApplicationChampion },
  { type: TagTypes.OrganizationApplicationChampionsWithEmployees },
  { type: TagTypes.OrganizationRequestTypeCategoryChampion },
  { type: TagTypes.RequestTypeCategoryChampion },
] as const;

const transformEmployeeLeave = (
  leave: EmployeeLeaveResponseDto
): EmployeeLeave => ({
  id: leave.id,
  employeeId: leave.employee_id,
  startDate: leave.start_date,
  returnDate: leave.return_date,
  status: leave.status,
  defaultAssigneeId: leave.default_assignee_id,
  forwardeeEmployeeId: leave.forwardee_employee_id,
  championReassignments: (leave.champion_reassignments ?? []).map(
    reassignment => ({
      id: reassignment.id,
      substituteChampionId: reassignment.substitute_champion_id,
      requestCategoryId: reassignment.request_category_id,
    })
  ),
  createdAt: leave.created_at,
  updatedAt: leave.updated_at,
});

// Marks the `getEmployeeFromDirectory` error for an employee missing from the
// directory. The directory is division-scoped, so for a user with limited
// division access this means "not visible to you". Carried in `data` so other
// custom errors from this query are never mistaken for it.
export const EMPLOYEE_NOT_IN_DIRECTORY = 'EMPLOYEE_NOT_IN_DIRECTORY';

export const isEmployeeNotInDirectoryError = (
  isError: boolean | undefined,
  error: unknown
): boolean =>
  !!isError &&
  typeof error === 'object' &&
  error !== null &&
  'data' in error &&
  error.data === EMPLOYEE_NOT_IN_DIRECTORY;

export const employeeApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // The getMyEmployee slice will return the caller's employee in respect to the specified organization.
    // ** This does not require any backend permissions and should be used to load the active user's employee details **
    getMyEmployee: builder.query<Employee, string>({
      query: organizationId =>
        `/v1/organizations/${organizationId}/employees/me`,
      transformResponse: (employeeDto: EmployeeDto) =>
        transformEmployee(employeeDto),
      providesTags: (_results, _error, organizationId) =>
        // Tagging this with the organizationId since the result of this slice will change when
        // the organizationId is changed. When we switch organizations we should invalidate this
        // tag to force this slice to be re-fetched.
        providesById(TagTypes.Organization, organizationId),
    }),
    getActiveEmployeesXboardings: builder.query<
      ActiveXboardingEmployeeIds,
      string
    >({
      query: organizationId =>
        `/v1/organizations/${organizationId}/employees/active_xboardings`,
      providesTags: (_result, _error, organizationId) =>
        providesById(TagTypes.ActiveXboardingEmployeeIds, organizationId),
    }),

    getEmployeeFromDirectory: builder.query<
      EmployeeDirectoryListing,
      { employeeId: string; organizationId: string }
    >({
      queryFn: async (
        { employeeId, organizationId },
        queryApi,
        _extraOptions,
        _fetchWithBaseQuery
      ) => {
        const response = await queryApi.dispatch(
          organizationApiSlice.endpoints.getOrganizationEmployeeDirectory.initiate(
            {
              organizationId,
              status: [
                EmployeeStatusEnum.Active,
                EmployeeStatusEnum.Inactive,
                EmployeeStatusEnum.Unknown,
              ],
            }
          )
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const employees = Array.isArray(response.data) ? response.data : [];
        const employee = employees.find(e => e.id === employeeId);

        if (!employee) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: `Employee with id=${employeeId} not found`,
              data: EMPLOYEE_NOT_IN_DIRECTORY,
            } as FetchBaseQueryError,
          };
        }

        return {
          data: employee,
        };
      },
      providesTags: (_results, _error, { employeeId }) =>
        providesById(TagTypes.EmployeeDirectory, employeeId),
    }),
    // The getEmployee slice will return employee details for a specific employee.
    // ** This requires the employees:read permission and should only be used for users who have this **
    getEmployee: builder.query<
      Employee,
      { employeeId: string; organizationId: string }
    >({
      queryFn: async (
        { employeeId, organizationId },
        queryApi,
        _extraOptions,
        _fetchWithBaseQuery
      ) => {
        const response = await queryApi.dispatch(
          organizationApiSlice.endpoints.getOrganizationEmployees.initiate({
            organizationId,
            status: [
              EmployeeStatusEnum.Active,
              EmployeeStatusEnum.Inactive,
              EmployeeStatusEnum.Unknown,
            ],
          })
        );

        if (response.error) {
          return { error: response.error as FetchBaseQueryError };
        }

        const employee = (response.data as Employee[]).find(
          (employee: Employee) => employee.id === employeeId
        );

        if (!employee) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: `Employee with id=${employeeId} not found`,
            } as FetchBaseQueryError,
          };
        }

        return {
          data: employee,
        };
      },
      providesTags: (_results, _error, { employeeId }) =>
        providesById(TagTypes.Employee, employeeId),
    }),
    getEmployeeApplicationsAccounts: builder.query<
      ListEmployeeApplicationAccountsResponse,
      ListEmployeeApplicationAccountsArgs
    >({
      query: ({ employeeId, organizationId }) =>
        `/v1/organizations/${organizationId}/employees/${employeeId}/employee_application_accounts`,
      transformResponse: (
        response: ApiListResponseDto<EmployeeApplicationAccountResponseDto>
      ) => {
        const employeeApplicationAccounts = response.results.map(
          employeeApplicationAccount => {
            return {
              id: employeeApplicationAccount.id,
              organizationId: employeeApplicationAccount.organization_id,
              applicationId: employeeApplicationAccount.application_id,
              employeeId: employeeApplicationAccount.employee_id,
              createdAt: employeeApplicationAccount.created_at,
              updatedAt: employeeApplicationAccount.updated_at,
            };
          }
        );
        return {
          results: employeeApplicationAccounts,
          total: response.total ?? 0,
        };
      },
      providesTags: (_result, _error, { employeeId }) => [
        { type: TagTypes.EmployeeApplicationAccount, id: employeeId },
      ],
    }),
    getApplicationEmployeeAccounts: builder.query<
      ListEmployeeApplicationAccountsResponse,
      GetApplicationEmployeeAccountsArgs
    >({
      query: ({ organizationId, applicationKey, limit, offset }) => ({
        url: `/v1/organizations/${organizationId}/applications/${applicationKey}/employee_application_accounts`,
        params: {
          limit,
          offset: offset ?? 0,
        },
      }),
      transformResponse: (
        response: ApiListResponseDto<EmployeeApplicationAccountResponseDto>
      ) => ({
        results: response.results.map(transformEmployeeApplicationAccount),
        total: response.total ?? 0,
      }),
      providesTags: (_result, _error, { applicationKey }) => [
        { type: TagTypes.EmployeeApplicationAccount, id: applicationKey },
      ],
    }),
    /**
     * Fetches employee application accounts for an application with full employee data.
     *
     * Combines data from multiple endpoints:
     * - Application employee accounts
     * - Full employee details for each account
     */
    getApplicationEmployeeAccountsWithEmployees: builder.query<
      ApplicationEmployeeAccountsWithEmployeesResponse,
      GetApplicationEmployeeAccountsArgs
    >({
      async queryFn(
        { organizationId, applicationKey, limit, offset },
        _queryApi,
        _extraOptions,
        fetchWithBaseQuery
      ) {
        try {
          // Fetch application employee accounts
          const accountsResponse = await fetchWithBaseQuery({
            url: `/v1/organizations/${organizationId}/applications/${applicationKey}/employee_application_accounts`,
            params: {
              limit: limit,
              offset: offset,
            },
          });

          if (accountsResponse.error) {
            return { error: accountsResponse.error as FetchBaseQueryError };
          }

          const accountsData =
            accountsResponse.data as ApiListResponseDto<EmployeeApplicationAccountResponseDto>;
          const accounts = accountsData.results.map(
            transformEmployeeApplicationAccount
          );

          // Get unique employee IDs
          const uniqueEmployeeIds = [
            ...new Set(accounts.map(account => account.employeeId)),
          ];

          // Fetch employee data for each unique employee
          // TODO: Replace with bulk fetch employees https://electricai.atlassian.net/browse/HY-2182
          const employeePromises = uniqueEmployeeIds.map(employeeId =>
            fetchWithBaseQuery(
              `/v1/organizations/${organizationId}/employees/${employeeId}`
            )
          );

          const employeeResponses = await Promise.all(employeePromises);

          // Build employee map for efficient lookups
          const employeeMap = new Map<string, Employee>();
          employeeResponses.forEach((response, index) => {
            if (response.data) {
              const employee = transformEmployee(response.data as EmployeeDto);
              employeeMap.set(uniqueEmployeeIds[index], employee);
            }
          });

          // Combine accounts with employee data
          const resultsWithEmployees = accounts.map(account => ({
            ...account,
            employee: employeeMap.get(account.employeeId) ?? null,
          }));

          return {
            data: {
              results: resultsWithEmployees,
              total: accountsData.total ?? 0,
            },
          };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: `Failed to fetch application employee accounts with employees: ${String(error)}`,
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: (_result, _error, { applicationKey }) => [
        { type: TagTypes.EmployeeApplicationAccount, id: applicationKey },
        TagTypes.Employee,
      ],
    }),
    getBulkEmployeeApplicationsAccounts: builder.query<
      BulkEmployeeApplicationAccountsResponse,
      BulkEmployeeApplicationAccountsArgs
    >({
      query: ({ employeeIds, organizationId }) => ({
        url: `/v1/organizations/${organizationId}/employees/employee_application_accounts/search`,
        method: 'POST',
        body: {
          employee_ids: employeeIds,
        },
      }),
      transformResponse: (
        response: BulkEmployeeApplicationAccountGetResponse
      ) => {
        const employeeApplicationAccountsMapping: Record<
          string,
          EmployeeApplicationAccount[]
        > = {};
        Object.entries(response.employee_application_accounts_mapping).forEach(
          ([employeeId, employeeApplicationAccounts]) => {
            employeeApplicationAccountsMapping[employeeId] =
              employeeApplicationAccounts.map(
                transformEmployeeApplicationAccount
              );
          }
        );
        return {
          employeeApplicationAccountsMapping,
        };
      },
      providesTags: (_result, _error, { employeeIds }) => [
        { type: TagTypes.EmployeeApplicationAccount, id: 'LIST' },
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeApplicationAccount as const,
          id: employeeId,
        })),
      ],
    }),
    getBulkEmployees: builder.query<BulkEmployeesResponse, BulkEmployeesArgs>({
      query: ({ employeeIds, organizationId }) => ({
        url: `/v1/organizations/${organizationId}/employees/bulk`,
        method: 'POST',
        body: {
          employee_ids: employeeIds,
        },
      }),
      transformResponse: (response: BulkEmployeesResponseDto) => {
        const employeesMapping: Record<string, Employee> = {};
        Object.entries(response.employees_mapping).forEach(
          ([employeeId, employeeDto]) => {
            employeesMapping[employeeId] = transformEmployee(employeeDto);
          }
        );
        return {
          employeesMapping,
        };
      },
      providesTags: (_result, _error, { employeeIds }) => [
        { type: TagTypes.Employee, id: 'LIST' },
        ...employeeIds.map(employeeId => ({
          type: TagTypes.Employee as const,
          id: employeeId,
        })),
      ],
    }),
    getEmployeeByEmail: builder.query<Employee, GetEmployeeByEmailArgs>({
      query: ({ email, organizationId }) =>
        `/v1/organizations/${organizationId}/employees/email/${email}`,
      transformResponse: (employeeDto: EmployeeDto) =>
        transformEmployee(employeeDto),
    }),
    getEmployeeById: builder.query<Employee, GetEmployeeByIdArgs>({
      query: ({ employeeId, organizationId }) =>
        `/v1/organizations/${organizationId}/employees/${employeeId}`,
      transformResponse: (employeeDto: EmployeeDto) =>
        transformEmployee(employeeDto),
    }),
    getOrganizationGroupsForEmployee: builder.query<
      Group[],
      GetGroupsForEmployeeArgs
    >({
      query: ({ organizationId, employeeId }) => {
        return {
          url: `/v1/organizations/${organizationId}/employees/${employeeId}/groups`,
        };
      },
      providesTags: (result, _error, { employeeId }) => [
        ...providesListAndAllById(TagTypes.Group)(result),
        { type: TagTypes.EmployeeGroups, id: employeeId },
      ],
      transformResponse: (response: GroupDto[]) => {
        return response?.map(group => {
          return {
            id: group.id,
            name: group.name,
            createdAt: group.created_at,
            updatedAt: group.updated_at,
            parentGroupId: group.parent_group_id ?? '',
            importEnabled: group.import_enabled,
            hrisGroupIntegration: group.hris_group_integration
              ? {
                  sourceId: group.hris_group_integration.source_id,
                  hrisIntegrationId:
                    group.hris_group_integration.hris_integration_id,
                }
              : undefined,
            applicationsCount: group.applications_count,
            employeesCount: group.employees_count,
            joinAutomationPreference: group.join_automation_preference ?? null,
            leaveAutomationPreference:
              group.leave_automation_preference ?? null,
            membershipType: group.membership_type,
          };
        });
      },
    }),
    sendDesktopAppInstructions: builder.mutation<
      void,
      DesktopAppInstructionsArgs
    >({
      query: ({ employeeIds, organizationId }) => ({
        url: `/v1/organizations/${organizationId}/desktop_app/employees_app_instructions`,
        method: 'POST',
        body: {
          employee_ids: employeeIds,
        },
      }),
    }),
    sendMDMEnrollInstructions: builder.mutation<
      void,
      MDMEnrollInstructionsArgs
    >({
      query: ({ employeeIds, organizationId }) => ({
        url: `/v1/organizations/${organizationId}/desktop_app/mdm_agent_install_instructions`,
        method: 'POST',
        body: {
          employee_ids: employeeIds,
        },
      }),
    }),
    sendUnenrollDeviceEmail: builder.mutation<
      void,
      { employeeId: string; organizationId: string }
    >({
      query: ({ employeeId, organizationId }) => ({
        url: `/v1/organizations/${organizationId}/desktop_app/unenroll_device`,
        method: 'POST',
        body: {
          employee_id: employeeId,
        },
      }),
    }),
    sendWelcomeEmails: builder.mutation<
      void,
      { employeeIds?: string[]; organizationId: string }
    >({
      query: ({ employeeIds = [], organizationId }) => ({
        url: `/v1/organizations/${organizationId}/employees/send_welcome_emails`,
        method: 'POST',
        body: {
          employee_ids: employeeIds,
        },
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: TagTypes.AdditionalInfo, id: organizationId },
      ],
    }),
    updateEmployee: builder.mutation<
      void,
      EmployeeUpdateArgs & { organizationId: string }
    >({
      query: ({
        id,
        firstName,
        lastName,
        jobTitle,
        email,
        personalEmail,
        startDate,
        organizationId,
        address,
        phone,
        status,
        organizationPackageId,
        managerId,
        deviceNotRequired,
      }) => ({
        url: `/v1/organizations/${organizationId}/employees/${id}`,
        method: 'PATCH',
        body: {
          first_name: firstName,
          last_name: lastName,
          job_title: jobTitle,
          email,
          personal_email: personalEmail,
          start_date: startDate,
          address: transformAddressPayload(address),
          phone_number: phone,
          status,
          organization_package_id: organizationPackageId,
          manager_id: managerId,
          device_not_required: deviceNotRequired,
        },
      }),
      invalidatesTags: (_results, _error, args) => [
        TagTypes.Employee,
        TagTypes.EmployeeWithDevice,
        TagTypes.Asset,
        TagTypes.DeviceMetrics,
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
      ],
    }),
    bulkUpdateEmployees: builder.mutation<
      void,
      { organizationId: string; employees: EmployeeUpdateArgs[] }
    >({
      query: ({ organizationId, employees }) => {
        return {
          url: `/v1/organizations/${organizationId}/employees`,
          method: 'PATCH',
          body: {
            employees: employees.map(employee => ({
              id: employee.id,
              first_name: employee.firstName,
              last_name: employee.lastName,
              job_title: employee.jobTitle,
              personal_email: employee.personalEmail,
              start_date: employee.startDate,
              address: transformAddressPayload(employee.address),
              phone_number: employee.phone,
              organization_package_id: employee.organizationPackageId,
              status: employee.status,
            })),
          },
        };
      },
      invalidatesTags: (_results, _error, args) => [
        TagTypes.Employee,
        TagTypes.EmployeeWithDevice,
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
      ],
    }),
    bulkUpdateEmployeesV2: builder.mutation<
      string,
      { organizationId: string; employees: EmployeeUpdateArgs[] }
    >({
      query: ({ organizationId, employees }) => {
        return {
          url: `/v2/organizations/${organizationId}/employees`,
          method: 'PATCH',
          body: {
            employees: employees.map(employee => ({
              id: employee.id,
              first_name: employee.firstName,
              last_name: employee.lastName,
              job_title: employee.jobTitle,
              personal_email: employee.personalEmail,
              start_date: employee.startDate,
              address: transformAddressPayload(employee.address),
              phone_number: employee.phone,
              organization_package_id: employee.organizationPackageId,
              status: employee.status,
            })),
          },
        };
      },
      transformResponse: (response: { workflow_id: string }) =>
        response.workflow_id,
      invalidatesTags: (_results, _error, args) => [
        TagTypes.Employee,
        TagTypes.EmployeeWithDevice,
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
      ],
    }),
    bulkUpdateEmployeeStatus: builder.mutation<
      void,
      { organizationId: string; employees: EmployeeStatusUpdateArgs[] }
    >({
      query: ({ organizationId, employees }) => {
        return {
          url: `/v1/organizations/${organizationId}/employees/status`,
          method: 'PATCH',
          body: {
            employees: employees.map(({ deactivationCause, ...rest }) => ({
              ...rest,
              ...(deactivationCause && {
                deactivation_cause: deactivationCause,
              }),
            })),
          },
        };
      },
      invalidatesTags: (_results, _error, args) => [
        TagTypes.Employee,
        TagTypes.EmployeeWithDevice,
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
        ...args.employees.map(e => ({
          type: TagTypes.EmployeeStatusChangeLog as const,
          id: e.id,
        })),
      ],
    }),
    removeEmployeeApplicationAccount: builder.mutation<
      void,
      { organizationId: string; employeeId: string; applicationIds: string[] }
    >({
      query: ({ organizationId, employeeId, applicationIds }) => ({
        url: `/v1/organizations/${organizationId}/employees/${employeeId}/employee_application_accounts`,
        method: 'DELETE',
        body: {
          application_keys: applicationIds,
        },
      }),
      invalidatesTags: (_results, _error, { employeeId }) => [
        ...invalidatesListAndById(
          TagTypes.EmployeeApplicationAccount,
          employeeId
        ),
        { type: TagTypes.EmployeeApplicationsAndRequests, id: employeeId },
        { type: TagTypes.GroupsAndApplicationsForEmployee, id: employeeId },
        TagTypes.Request,
      ],
    }),
    removeBulkEmployeeApplicationAccounts: builder.mutation<
      void,
      {
        organizationId: string;
        employeeIds: string[];
        applicationIds: string[];
        skipProvisioning?: boolean;
      }
    >({
      query: ({
        organizationId,
        employeeIds,
        applicationIds,
        skipProvisioning,
      }) => ({
        url: `/v1/organizations/${organizationId}/employees/employee_application_accounts`,
        method: 'DELETE',
        body: {
          employee_ids: employeeIds,
          application_keys: applicationIds,
          skip_provisioning: skipProvisioning ?? false,
        },
      }),
      invalidatesTags: (_results, _error, { employeeIds }) => [
        ...invalidatesList(TagTypes.EmployeeApplicationAccount),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeApplicationAccount as const,
          id: employeeId,
        })),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeApplicationsAndRequests as const,
          id: employeeId,
        })),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.GroupsAndApplicationsForEmployee as const,
          id: employeeId,
        })),
        TagTypes.Request,
      ],
    }),
    addEmployeeApplicationAccounts: builder.mutation<
      void,
      {
        organizationId: string;
        employeeId: string;
        applicationIds: string[];
        skipProvisioning?: boolean;
      }
    >({
      query: ({
        organizationId,
        employeeId,
        applicationIds,
        skipProvisioning,
      }) => ({
        url: `/v1/organizations/${organizationId}/employees/${employeeId}/employee_application_accounts`,
        method: 'POST',
        body: {
          application_keys: applicationIds,
          skip_provisioning: skipProvisioning ?? false,
        },
      }),
      invalidatesTags: (_results, _error, { employeeId }) => [
        ...invalidatesListAndById(
          TagTypes.EmployeeApplicationAccount,
          employeeId
        ),
        { type: TagTypes.EmployeeApplicationsAndRequests, id: employeeId },
        { type: TagTypes.GroupsAndApplicationsForEmployee, id: employeeId },
        TagTypes.Request,
      ],
    }),
    addBulkEmployeeApplicationAccounts: builder.mutation<
      void,
      {
        organizationId: string;
        employeeIds: string[];
        applicationIds: string[];
        skipProvisioning?: boolean;
      }
    >({
      query: ({
        organizationId,
        employeeIds,
        applicationIds,
        skipProvisioning,
      }) => ({
        url: `/v1/organizations/${organizationId}/employees/employee_application_accounts`,
        method: 'POST',
        body: {
          employee_ids: employeeIds,
          application_keys: applicationIds,
          skip_provisioning: skipProvisioning ?? false,
        },
      }),
      invalidatesTags: (_results, _error, { employeeIds }) => [
        ...invalidatesList(TagTypes.EmployeeApplicationAccount),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeApplicationAccount as const,
          id: employeeId,
        })),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeApplicationsAndRequests as const,
          id: employeeId,
        })),
        ...employeeIds.map(employeeId => ({
          type: TagTypes.GroupsAndApplicationsForEmployee as const,
          id: employeeId,
        })),
        TagTypes.Request,
      ],
    }),
    getEmployeeStatusChangeLog: builder.query<
      EmployeeStatusChangeLogResponse,
      { organizationId: string; employeeId: string; limit?: number }
    >({
      query: ({ organizationId, employeeId, limit = 1 }) =>
        `/v1/organizations/${organizationId}/employees/${employeeId}/status-change-history?limit=${limit}&offset=0`,
      transformResponse: (response: {
        results: Array<EmployeeStatusChangeLogResponseDto>;
        total: number;
      }): EmployeeStatusChangeLogResponse => {
        return {
          results: response.results.map(log => ({
            id: log.id,
            employeeId: log.employee_id,
            oldStatus: log.old_status,
            newStatus: log.new_status,
            deactivationCause: log.deactivation_cause as
              | DeactivationCauseEnum
              | undefined,
            createdAt: log.created_at,
            updatedAt: log.updated_at ?? '',
          })),
          total: response.total ?? 0,
        };
      },
      providesTags: (_result, _error, { employeeId }) =>
        providesById(TagTypes.EmployeeStatusChangeLog, employeeId),
    }),
    getEmployeeLeaves: builder.query<
      EmployeeLeaveResponse,
      { organizationId: string; employeeId: string }
    >({
      query: ({ organizationId, employeeId }) =>
        `/v1/organizations/${organizationId}/employees/${employeeId}/leaves`,
      transformResponse: (
        response: PaginatedResponseEmployeeLeaveResponseDto
      ): EmployeeLeaveResponse => {
        return {
          results: response.results.map(transformEmployeeLeave),
          total: response.total ?? 0,
        };
      },
      providesTags: (_result, _error, { employeeId }) =>
        providesById(TagTypes.EmployeeLeave, employeeId),
    }),
    getBulkEmployeeLeaves: builder.query<
      Record<string, EmployeeLeave[]>,
      {
        organizationId: string;
        employeeIds: string[];
        statuses?: EmployeeLeaveStatusEnum[];
      }
    >({
      query: ({ organizationId, employeeIds, statuses }) => ({
        url: `/v1/organizations/${organizationId}/employees/leaves/bulk`,
        method: 'POST',
        body: {
          employee_ids: employeeIds,
          ...(statuses ? { statuses } : {}),
        },
      }),
      transformResponse: (response: BulkGetEmployeeLeavesResponseDto) => {
        const result: Record<string, EmployeeLeave[]> = {};
        Object.entries(response.employee_leaves).forEach(
          ([employeeId, leaves]) => {
            result[employeeId] = leaves.map(transformEmployeeLeave);
          }
        );
        return result;
      },
      providesTags: (_result, _error, { employeeIds }) => [
        { type: TagTypes.EmployeeLeave, id: 'LIST' },
        ...employeeIds.map(employeeId => ({
          type: TagTypes.EmployeeLeave as const,
          id: employeeId,
        })),
      ],
    }),
    getEmployeeLeaveById: builder.query<
      EmployeeLeave,
      { organizationId: string; employeeId: string; leaveId: string }
    >({
      query: ({ organizationId, employeeId, leaveId }) =>
        `/v1/organizations/${organizationId}/employees/${employeeId}/leaves/${leaveId}`,
      transformResponse: transformEmployeeLeave,
      providesTags: (_result, _error, { leaveId }) =>
        providesById(TagTypes.EmployeeLeave, leaveId),
    }),
    createEmployeeLeave: builder.mutation<
      EmployeeLeave,
      {
        organizationId: string;
        employeeId: string;
        startDate: string;
        returnDate?: string;
        defaultAssigneeId?: string;
        forwardeeEmployeeId?: string;
        oooAutoResponderEnabled?: boolean;
        oooAutoResponderMessage?: string;
        leaveChampionReassignments?: {
          substituteChampionId: string;
          requestCategoryId: string;
        }[];
      }
    >({
      query: ({
        organizationId,
        employeeId,
        startDate,
        returnDate,
        defaultAssigneeId,
        forwardeeEmployeeId,
        oooAutoResponderEnabled,
        oooAutoResponderMessage,
        leaveChampionReassignments,
      }) => ({
        url: `/v1/organizations/${organizationId}/employees/${employeeId}/leaves`,
        method: 'POST',
        body: {
          start_date: startDate,
          return_date: returnDate,
          default_assignee_id: defaultAssigneeId,
          forwardee_employee_id: forwardeeEmployeeId,
          ooo_auto_responder_enabled: oooAutoResponderEnabled,
          ooo_auto_responder_message: oooAutoResponderMessage,
          champion_reassignments: leaveChampionReassignments?.map(
            reassignment => ({
              substitute_champion_id: reassignment.substituteChampionId,
              request_category_id: reassignment.requestCategoryId,
            })
          ),
        } as CreateEmployeeLeaveRequest,
      }),
      transformResponse: transformEmployeeLeave,
      invalidatesTags: (_result, error, { employeeId }) => [
        ...invalidatesListAndById(TagTypes.EmployeeLeave, employeeId),
        ...(error ? [] : CHAMPION_INVALIDATION_TAGS),
      ],
    }),
    updateEmployeeLeave: builder.mutation<
      EmployeeLeave,
      {
        organizationId: string;
        employeeId: string;
        leaveId: string;
        startDate: string;
        returnDate: string | null;
      }
    >({
      query: ({
        organizationId,
        employeeId,
        leaveId,
        startDate,
        returnDate,
      }) => ({
        url: `/v1/organizations/${organizationId}/employees/${employeeId}/leaves/${leaveId}`,
        method: 'PATCH',
        // return_date is sent as an explicit `null` (rather than omitted) to
        // clear it - the backend only treats a present-but-null value as a
        // clear instruction, and leaves it unchanged if the key is absent.
        body: {
          start_date: startDate,
          return_date: returnDate,
        },
      }),
      transformResponse: transformEmployeeLeave,
      // Also invalidate by leaveId - getEmployeeLeaveById provides its tag
      // keyed by leaveId, not employeeId, so it would otherwise keep
      // serving the pre-edit dates from cache (e.g. on the Return to Work
      // page).
      // updateEmployeeLeave only edits start/return dates - it never touches
      // champion reassignments server-side (see employee_leave_service.py
      // update_employee_leave), so it doesn't invalidate champion tags.
      invalidatesTags: (_result, _error, { employeeId, leaveId }) => [
        ...invalidatesListAndById(TagTypes.EmployeeLeave, employeeId),
        { type: TagTypes.EmployeeLeave, id: leaveId },
      ],
    }),
    endEmployeeLeaveEarly: builder.mutation<
      EmployeeLeave,
      { organizationId: string; employeeId: string; leaveId: string }
    >({
      query: ({ organizationId, employeeId, leaveId }) => ({
        url: `/v1/organizations/${organizationId}/employees/${employeeId}/leaves/${leaveId}/end-early`,
        method: 'POST',
      }),
      transformResponse: transformEmployeeLeave,
      // Also invalidate by leaveId - getEmployeeLeaveById provides its tag
      // keyed by leaveId, not employeeId, so it would otherwise keep
      // serving the pre-edit return date from cache.
      invalidatesTags: (_result, error, { employeeId, leaveId }) => [
        ...invalidatesListAndById(TagTypes.EmployeeLeave, employeeId),
        { type: TagTypes.EmployeeLeave, id: leaveId },
        ...(error ? [] : CHAMPION_INVALIDATION_TAGS),
      ],
    }),
    cancelEmployeeLeave: builder.mutation<
      EmployeeLeave,
      { organizationId: string; employeeId: string; leaveId: string }
    >({
      query: ({ organizationId, employeeId, leaveId }) => ({
        url: `/v1/organizations/${organizationId}/employees/${employeeId}/leaves/${leaveId}/cancel`,
        method: 'POST',
      }),
      transformResponse: transformEmployeeLeave,
      invalidatesTags: (_result, _error, { employeeId, leaveId }) => [
        ...invalidatesListAndById(TagTypes.EmployeeLeave, employeeId),
        { type: TagTypes.EmployeeLeave, id: leaveId },
        { type: TagTypes.OrganizationApplicationChampion },
        { type: TagTypes.OrganizationApplicationChampionsWithEmployees },
        { type: TagTypes.OrganizationRequestTypeCategoryChampion },
        { type: TagTypes.RequestTypeCategoryChampion },
      ],
    }),
  }),
});

const transformAddress = (address: EmployeeDto['address']) => {
  if (!address) {
    return undefined;
  }

  return {
    streetAddress1: address.street_1 ?? '',
    streetAddress2: address.street_2 ?? '',
    city: address.city ?? '',
    country: address.country ?? '',
    state: address.state ?? '',
    zip: address.zip_code ?? '',
  };
};

const transformAddressPayload = (address: Address | null | undefined) => {
  if (!address) {
    return address;
  }

  return {
    street_1: address.streetAddress1,
    street_2: address.streetAddress2,
    city: address.city,
    country: address.country,
    state: address.state,
    zip_code: address.zip,
  };
};

export const transformEmployee = (employeeDto: EmployeeDto): Employee => {
  return {
    id: employeeDto?.id,
    firstName: employeeDto?.first_name,
    preferredFirstName: employeeDto?.preferred_first_name,
    lastName: employeeDto?.last_name,
    email: employeeDto?.email ?? null,
    personalEmail: employeeDto?.personal_email,
    jobTitle: employeeDto?.job_title,
    jobTitleFrCa: employeeDto?.job_title_fr_ca,
    userId: employeeDto?.user_id,
    createdAt: employeeDto?.created_at,
    updatedAt: employeeDto?.updated_at,
    startDate: employeeDto?.start_date,
    status: employeeDto?.status,
    source: employeeDto?.source as EmployeeSource,
    roles:
      employeeDto?.roles?.map(role => ({
        id: role.id,
        slug: role.slug as OrganizationUserRoleSlugs,
      })) || [],
    hrisIntegration:
      employeeDto?.hris_employee_integrations?.length &&
      employeeDto?.hris_employee_integrations?.length > 0
        ? { sourceId: employeeDto?.hris_employee_integrations[0].source_id }
        : undefined,
    address: transformAddress(employeeDto?.address),
    phone: employeeDto?.phone_number,
    organizationPackageId: employeeDto?.organization_package_id,
    managerId: employeeDto?.manager_id,
    terminationDate: employeeDto?.termination_date,
    groupIds: employeeDto?.group_ids,
    deviceNotRequired: employeeDto?.device_not_required === true,
  };
};

export const transformEmployeeDirectoryListing = (
  employeeDirectoryListingDto: EmployeeDirectoryListingDto
): EmployeeDirectoryListing => {
  return {
    id: employeeDirectoryListingDto.id,
    firstName: employeeDirectoryListingDto.first_name,
    preferredFirstName: employeeDirectoryListingDto.preferred_first_name,
    lastName: employeeDirectoryListingDto.last_name,
    email: employeeDirectoryListingDto.email ?? null,
    jobTitle: employeeDirectoryListingDto.job_title,
    jobTitleFrCa: employeeDirectoryListingDto.job_title_fr_ca,
    status: employeeDirectoryListingDto.status,
    startDate: employeeDirectoryListingDto.start_date,
  };
};

const transformEmployeeApplicationAccount = (
  employeeApplicationAccountDto: EmployeeApplicationAccountResponseDto
): EmployeeApplicationAccount => {
  return {
    id: employeeApplicationAccountDto.id,
    organizationId: employeeApplicationAccountDto.organization_id,
    employeeId: employeeApplicationAccountDto.employee_id,
    applicationId: employeeApplicationAccountDto.application_id,
    createdAt: employeeApplicationAccountDto.created_at,
    updatedAt: employeeApplicationAccountDto.updated_at,
  };
};

// export auto-generated hooks
export const {
  useGetMyEmployeeQuery,
  useLazyGetMyEmployeeQuery,
  useGetEmployeeQuery,
  useLazyGetEmployeeQuery,
  useGetEmployeeFromDirectoryQuery,
  useLazyGetEmployeeFromDirectoryQuery,
  useGetEmployeeApplicationsAccountsQuery,
  useLazyGetEmployeeApplicationsAccountsQuery,
  useGetApplicationEmployeeAccountsQuery,
  useLazyGetApplicationEmployeeAccountsQuery,
  useGetApplicationEmployeeAccountsWithEmployeesQuery,
  useLazyGetApplicationEmployeeAccountsWithEmployeesQuery,
  useGetBulkEmployeeApplicationsAccountsQuery,
  useLazyGetBulkEmployeeApplicationsAccountsQuery,
  useGetBulkEmployeesQuery,
  useLazyGetBulkEmployeesQuery,
  useGetEmployeeByEmailQuery,
  useLazyGetEmployeeByEmailQuery,
  useGetEmployeeByIdQuery,
  useLazyGetEmployeeByIdQuery,
  useGetOrganizationGroupsForEmployeeQuery,
  useLazyGetOrganizationGroupsForEmployeeQuery,
  useSendDesktopAppInstructionsMutation,
  useSendMDMEnrollInstructionsMutation,
  useUpdateEmployeeMutation,
  useBulkUpdateEmployeesMutation,
  useBulkUpdateEmployeesV2Mutation,
  useBulkUpdateEmployeeStatusMutation,
  useSendUnenrollDeviceEmailMutation,
  useSendWelcomeEmailsMutation,
  useGetActiveEmployeesXboardingsQuery,
  useRemoveEmployeeApplicationAccountMutation,
  useRemoveBulkEmployeeApplicationAccountsMutation,
  useAddEmployeeApplicationAccountsMutation,
  useAddBulkEmployeeApplicationAccountsMutation,
  useGetEmployeeStatusChangeLogQuery,
  useGetEmployeeLeavesQuery,
  useGetBulkEmployeeLeavesQuery,
  useLazyGetBulkEmployeeLeavesQuery,
  useGetEmployeeLeaveByIdQuery,
  useCreateEmployeeLeaveMutation,
  useUpdateEmployeeLeaveMutation,
  useEndEmployeeLeaveEarlyMutation,
  useCancelEmployeeLeaveMutation,
} = employeeApiSlice;
